import React, { useState, useEffect, useCallback, useRef } from 'react';
import { VoiceTestClient } from '../../services/voiceTestClient';
import api from '../../services/api';

function SparklesIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 3l1.912 5.813a2 2 0 0 0 1.275 1.275L21 12l-5.813 1.912a2 2 0 0 0-1.275 1.275L12 21l-1.912-5.813a2 2 0 0 0-1.275-1.275L3 12l5.813-1.912a2 2 0 0 0 1.275-1.275L12 3z" />
    </svg>
  );
}

const SCENARIOS = [
  { name: 'schedule-cleaning', label: 'Schedule Cleaning' },
  { name: 'cancel-appointment', label: 'Cancel Appointment' },
  { name: 'request-callback', label: 'Request Callback' },
  { name: 'opt-out', label: 'Opt Out' },
  { name: 'new-patient-booking', label: 'New Patient Booking' }
];

const AUTO_SCENARIOS_LIST = [
  { name: 'auto-schedule-appointment', label: 'AI: Schedule Appointment' },
  { name: 'auto-cancel-appointment', label: 'AI: Cancel Appointment' },
  { name: 'auto-new-patient-booking', label: 'AI: New Patient Booking' },
  { name: 'auto-reschedule-appointment', label: 'AI: Reschedule Appointment' },
  { name: 'auto-request-callback', label: 'AI: Request Callback' },
  { name: 'security-identity-bypass', label: 'Security: Identity Bypass' },
  { name: 'security-prompt-injection', label: 'Security: Prompt Injection' },
  { name: 'security-cross-patient', label: 'Security: Cross-Patient Data' },
  { name: 'security-skip-verification', label: 'Security: Skip Verification' }
];

function VoiceAgentTab({ practiceId }) {
  const [sessionActive, setSessionActive] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [mode, setMode] = useState('mock');
  const [assistantName, setAssistantName] = useState('');
  const [selectedScenario, setSelectedScenario] = useState('');
  const [scenarioRunning, setScenarioRunning] = useState(false);
  const [selectedAutoScenario, setSelectedAutoScenario] = useState('');
  const [autoTestRunning, setAutoTestRunning] = useState(false);
  const [autoTestMode, setAutoTestMode] = useState('mock');
  // Push Slots state
  const [pushSlotsExpanded, setPushSlotsExpanded] = useState(false);
  const [providers, setProviders] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [slotProvider, setSlotProvider] = useState('');
  const [slotDepartment, setSlotDepartment] = useState('');
  const [slotDate, setSlotDate] = useState('');
  const [slotTime, setSlotTime] = useState('09:00');
  const [slotReason, setSlotReason] = useState('New Telemed Wtloss 45');
  const [slotReasonId, setSlotReasonId] = useState('81');
  const [slotApptTypeId, setSlotApptTypeId] = useState('683');
  const [slotDuration, setSlotDuration] = useState('45');
  const [slotCount, setSlotCount] = useState('1');
  const [slotInterval, setSlotInterval] = useState('60');
  const [pushingSlots, setPushingSlots] = useState(false);
  const [, setBookingConfigs] = useState([]);
  const [ehrAppointmentTypes, setEhrAppointmentTypes] = useState([]);
  const [ehrReasonCodes, setEhrReasonCodes] = useState([]);
  // Simulate Booking state
  const [simulateBookingExpanded, setSimulateBookingExpanded] = useState(false);
  const [openSlots, setOpenSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [bookingPatientId, setBookingPatientId] = useState('5');
  const [simulatingBooking, setSimulatingBooking] = useState(false);
  const clientRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const addMessage = useCallback((msg) => {
    setMessages(prev => {
      // Deduplicate: skip if same type + text as last message
      const last = prev[prev.length - 1];
      if (last && last.type === msg.type && last.text && last.text === msg.text) {
        return prev;
      }
      return [...prev, { ...msg, id: Date.now() + Math.random() }];
    });
  }, []);

  // Load providers & departments for push-slots
  useEffect(() => {
    if (!practiceId) return;
    api.request(`/practices/${practiceId}/voice/providers`)
      .then(res => setProviders((res.data || res || []).filter(p => p.enabled !== false)))
      .catch(() => {});
    api.request(`/practices/${practiceId}/voice/departments`)
      .then(res => setDepartments((res.data || res || []).filter(d => d.athena_department_id)))
      .catch(() => {});
    api.request(`/practices/${practiceId}/voice/booking-configs`)
      .then(res => {
        const configs = res.data || res || [];
        setBookingConfigs(configs);
        // Auto-populate defaults from first config
        const first = configs[0];
        if (first) {
          setSlotReasonId(String(first.reason_code || '81'));
          setSlotApptTypeId(String(first.appointment_type_id || '683'));
          if (first.slot_keywords) setSlotReason(first.slot_keywords.split(',')[0].trim());
        }
      })
      .catch(() => {});
    api.request(`/practices/${practiceId}/ehr/appointment-types`)
      .then(res => setEhrAppointmentTypes((res.data || []).filter(t => t.enabled)))
      .catch(() => {});
    api.request(`/practices/${practiceId}/ehr/reason-codes`)
      .then(res => setEhrReasonCodes((res.data || []).filter(r => r.enabled)))
      .catch(() => {});
    api.request(`/practices/${practiceId}/ehr/open-slots`)
      .then(res => setOpenSlots(res.data || []))
      .catch(() => {});
    // Default date to tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setSlotDate(tomorrow.toISOString().split('T')[0]);
  }, [practiceId]);

  const handlePushSlots = async () => {
    if (pushingSlots) return;
    setPushingSlots(true);
    try {
      const count = parseInt(slotCount, 10) || 1;
      const duration = parseInt(slotDuration, 10) || 45;
      const interval = parseInt(slotInterval, 10) || 60;
      const [startH, startM] = slotTime.split(':').map(Number);

      const provider = providers.find(p => String(p.id) === slotProvider);
      const dept = departments.find(d => String(d.id) === slotDepartment);

      const slots = [];
      for (let i = 0; i < count; i++) {
        const dt = new Date(`${slotDate}T00:00:00Z`);
        dt.setUTCHours(startH, startM + (i * interval), 0, 0);
        slots.push({
          ID: parseInt(slotReasonId, 10) || 81,
          DateTime: dt.toISOString().replace('T', ' ').replace('.000Z', 'Z'),
          Reason: slotReason,
          Duration: duration,
          AppointmentTypeID: parseInt(slotApptTypeId, 10) || 683,
          Provider: {
            ID: String(provider?.athena_provider_id || '1'),
            IDType: 'EHRID',
            FirstName: provider?.first_name || 'Unknown',
            LastName: provider?.last_name || 'Provider'
          },
          Location: {
            Type: 'Outpatient',
            Department: String(dept?.athena_department_id || '21')
          }
        });
      }

      const res = await api.post(`/practices/${practiceId}/ehr/push-slots`, { slots });
      addMessage({
        type: 'system',
        text: `Pushed ${count} slot(s) to EHR: ${slotReason} with ${provider?.first_name || ''} ${provider?.last_name || ''} on ${slotDate} starting ${slotTime}`
      });
      addMessage({
        type: 'toolCall',
        toolName: 'pushSlots',
        toolInput: { slots: slots.length, date: slotDate, reason: slotReason, provider: `${provider?.first_name} ${provider?.last_name}` },
        toolResult: res.data || res,
        toolMode: 'live'
      });
    } catch (err) {
      addMessage({ type: 'system', text: `Push slots failed: ${err.message || err}` });
    }
    setPushingSlots(false);
    // Refresh open slots after pushing
    api.request(`/practices/${practiceId}/ehr/open-slots`).then(r => setOpenSlots(r.data || [])).catch(() => {});
  };

  const handleSimulateBooking = async () => {
    if (simulatingBooking || !selectedSlot || !bookingPatientId) return;
    setSimulatingBooking(true);
    try {
      const slot = openSlots.find(s => String(s.athena_appointment_id) === selectedSlot);
      const res = await api.post(`/practices/${practiceId}/ehr/simulate-booking`, {
        athenaAppointmentId: parseInt(selectedSlot, 10),
        athenaPatientId: parseInt(bookingPatientId, 10)
      });
      addMessage({
        type: 'system',
        text: `Simulated booking: Patient ${bookingPatientId} → Slot ${selectedSlot} (${slot?.appointment_type || 'unknown'})`
      });
      addMessage({
        type: 'toolCall',
        toolName: 'simulateBooking',
        toolInput: { slotId: selectedSlot, patientId: bookingPatientId, appointmentType: slot?.appointment_type },
        toolResult: res.data || res,
        toolMode: 'live'
      });
      // Refresh open slots list
      api.request(`/practices/${practiceId}/ehr/open-slots`).then(r => setOpenSlots(r.data || [])).catch(() => {});
      setSelectedSlot('');
    } catch (err) {
      addMessage({ type: 'system', text: `Simulate booking failed: ${err.message || err}` });
    }
    setSimulatingBooking(false);
  };

  const handleStartSession = async () => {
    setConnecting(true);
    setMessages([]);

    const client = new VoiceTestClient();
    clientRef.current = client;

    const token = localStorage.getItem('nh_auth_token');

    client.on('connect', () => {
      client.startSession({ practiceId, campaignType: 'inbound', mode });
    });

    client.on('sessionReady', (data) => {
      setConnecting(false);
      setSessionActive(true);
      setAssistantName(data.assistantName);
      addMessage({
        type: 'system',
        text: `Connected to ${data.assistantName} (${data.mode} mode)`
      });
      if (data.firstMessage) {
        addMessage({ type: 'assistant', text: data.firstMessage });
      }
      setTimeout(() => inputRef.current?.focus(), 100);
    });

    client.on('transcript', (data) => {
      // Streaming transcript — we'll use turnComplete for final message
    });

    client.on('toolCall', (data) => {
      addMessage({
        type: 'toolCall',
        toolName: data.name,
        toolInput: data.input,
        toolUseId: data.toolUseId
      });
    });

    client.on('toolResult', (data) => {
      setMessages(prev => prev.map(m =>
        m.type === 'toolCall' && m.toolUseId === data.toolUseId
          ? { ...m, toolResult: data.result, toolMode: data.mode }
          : m
      ));
    });

    client.on('turnComplete', (data) => {
      if (data.assistantText) {
        addMessage({ type: 'assistant', text: data.assistantText });
      }
    });

    client.on('scenarioProgress', (data) => {
      addMessage({
        type: 'system',
        text: `Scenario turn ${data.turn}/${data.total}: ${data.message}`
      });
    });

    client.on('scenarioComplete', (data) => {
      setScenarioRunning(false);
      addMessage({
        type: 'scenarioResult',
        scenario: data.scenario,
        passed: data.passed,
        assertions: data.assertions,
        turns: data.turns,
        durationMs: data.durationMs,
        error: data.error
      });
    });

    client.on('sessionEnded', () => {
      setSessionActive(false);
      setConnecting(false);
      addMessage({ type: 'system', text: 'Session ended' });
    });

    client.on('error', (data) => {
      setConnecting(false);
      addMessage({ type: 'system', text: `Error: ${data.message}` });
    });

    client.on('connect_error', (err) => {
      setConnecting(false);
      addMessage({ type: 'system', text: `Connection error: ${err.message}` });
    });

    client.connect({ token });
  };

  const handleEndSession = () => {
    if (clientRef.current) {
      clientRef.current.endSession();
      clientRef.current.disconnect();
      clientRef.current = null;
    }
    setSessionActive(false);
  };

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || !sessionActive) return;

    addMessage({ type: 'user', text: trimmed });
    clientRef.current?.sendMessage(trimmed);
    setInput('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleRunScenario = () => {
    if (!selectedScenario || scenarioRunning) return;
    setScenarioRunning(true);
    addMessage({ type: 'system', text: `Running scenario: ${selectedScenario}...` });

    // Scenarios need a fresh session — disconnect current and use runScenario
    if (clientRef.current) {
      clientRef.current.endSession();
      clientRef.current.disconnect();
    }
    setSessionActive(false);

    const client = new VoiceTestClient();
    clientRef.current = client;
    const token = localStorage.getItem('nh_auth_token');

    client.on('connect', () => {
      client.runScenario({ scenarioName: selectedScenario, practiceId, campaignType: 'inbound' });
    });

    client.on('scenarioProgress', (data) => {
      addMessage({
        type: 'system',
        text: `Turn ${data.turn}/${data.total}: ${data.message}`
      });
    });

    client.on('scenarioComplete', (data) => {
      setScenarioRunning(false);
      addMessage({
        type: 'scenarioResult',
        scenario: data.scenario,
        passed: data.passed,
        assertions: data.assertions,
        turns: data.turns,
        durationMs: data.durationMs,
        error: data.error
      });
      client.disconnect();
      clientRef.current = null;
    });

    client.on('error', (data) => {
      setScenarioRunning(false);
      addMessage({ type: 'system', text: `Scenario error: ${data.message}` });
    });

    client.on('connect_error', (err) => {
      setScenarioRunning(false);
      addMessage({ type: 'system', text: `Connection error: ${err.message}` });
    });

    client.connect({ token });
  };

  const handleRunAutoTest = () => {
    if (!selectedAutoScenario || autoTestRunning) return;
    setAutoTestRunning(true);
    addMessage({ type: 'system', text: `Starting AI Patient Test: ${selectedAutoScenario}...` });

    // Auto tests need their own session — disconnect any current
    if (clientRef.current) {
      clientRef.current.endSession();
      clientRef.current.disconnect();
    }
    setSessionActive(false);

    const client = new VoiceTestClient();
    clientRef.current = client;
    const token = localStorage.getItem('nh_auth_token');

    client.on('connect', () => {
      client.runAutoTest({
        practiceId,
        scenarioName: selectedAutoScenario,
        mode: autoTestMode,
        campaignType: 'inbound'
      });
    });

    client.on('autoTestProgress', (data) => {
      if (data.role === 'user') {
        addMessage({ type: 'user', text: data.text });
      } else if (data.role === 'assistant') {
        addMessage({ type: 'assistant', text: data.text });
      } else {
        addMessage({ type: 'system', text: data.text });
      }
      if (data.toolCalls?.length > 0) {
        for (const tc of data.toolCalls) {
          addMessage({
            type: 'toolCall',
            toolName: tc.name,
            toolInput: tc.input,
            toolResult: tc.result,
            toolMode: autoTestMode
          });
        }
      }
    });

    client.on('autoTestComplete', (data) => {
      setAutoTestRunning(false);
      addMessage({
        type: 'autoTestResult',
        runId: data.runId,
        scenario: data.scenario,
        passed: data.passed,
        score: data.score,
        evaluation: data.evaluation,
        toolAssertions: data.toolAssertions,
        turns: data.turns,
        durationMs: data.durationMs,
        error: data.error
      });
      client.disconnect();
      clientRef.current = null;
    });

    client.on('error', (data) => {
      setAutoTestRunning(false);
      addMessage({ type: 'system', text: `AI Test error: ${data.message}` });
    });

    client.on('connect_error', (err) => {
      setAutoTestRunning(false);
      addMessage({ type: 'system', text: `Connection error: ${err.message}` });
    });

    client.connect({ token });
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (clientRef.current) {
        clientRef.current.endSession();
        clientRef.current.disconnect();
      }
    };
  }, []);

  return (
    <div style={testStyles.container}>
      {/* Header */}
      <div style={testStyles.header}>
        <div style={testStyles.headerLeft}>
          <SparklesIcon />
          <span style={testStyles.headerTitle}>
            {assistantName ? `Testing: ${assistantName}` : 'Test Agent'}
          </span>
          <span style={{
            ...testStyles.statusBadge,
            backgroundColor: sessionActive ? '#dcfce7' : connecting ? '#fef3c7' : '#f3f4f6',
            color: sessionActive ? '#166534' : connecting ? '#92400e' : '#6b7280'
          }}>
            {sessionActive ? 'Connected' : connecting ? 'Connecting...' : 'Disconnected'}
          </span>
        </div>
        <div style={testStyles.headerRight}>
          {!sessionActive && !connecting && (
            <>
              <select
                value={mode}
                onChange={(e) => setMode(e.target.value)}
                style={testStyles.modeSelect}
              >
                <option value="mock">Mock Tools</option>
                <option value="live">Live Tools</option>
              </select>
              <button onClick={handleStartSession} style={testStyles.startButton}>
                Start Session
              </button>
            </>
          )}
          {(sessionActive || connecting) && (
            <button onClick={handleEndSession} style={testStyles.endButton}>
              End Session
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div style={testStyles.messagesArea}>
        {messages.length === 0 && !sessionActive && !connecting && (
          <div style={testStyles.emptyState}>
            <SparklesIcon />
            <h3 style={{ margin: '12px 0 8px', color: '#374151' }}>Voice AI Test Agent</h3>
            <p style={{ margin: 0, color: '#6b7280', fontSize: '14px', textAlign: 'center', maxWidth: '400px' }}>
              Start a test session to chat with your voice assistant using text.
              Tool calls will be mocked or sent to your live webhook.
            </p>
          </div>
        )}

        {messages.map((msg) => {
          if (msg.type === 'user') {
            return (
              <div key={msg.id} style={testStyles.userRow}>
                <div style={testStyles.userBubble}>{msg.text}</div>
              </div>
            );
          }
          if (msg.type === 'assistant') {
            return (
              <div key={msg.id} style={testStyles.assistantRow}>
                <div style={testStyles.assistantBubble}>{msg.text}</div>
              </div>
            );
          }
          if (msg.type === 'toolCall') {
            return (
              <div key={msg.id} style={testStyles.toolCard}>
                <div style={testStyles.toolHeader}>
                  <span style={testStyles.toolIcon}>&#9881;</span>
                  <span style={testStyles.toolName}>{msg.toolName}</span>
                  {msg.toolMode && (
                    <span style={{
                      ...testStyles.toolBadge,
                      backgroundColor: msg.toolMode === 'live' ? '#dbeafe' : '#f3e8ff',
                      color: msg.toolMode === 'live' ? '#1d4ed8' : '#7c3aed'
                    }}>{msg.toolMode}</span>
                  )}
                </div>
                <div style={testStyles.toolBody}>
                  <div style={testStyles.toolSection}>
                    <span style={testStyles.toolLabel}>Input:</span>
                    <pre style={testStyles.toolPre}>{JSON.stringify(msg.toolInput, null, 2)}</pre>
                  </div>
                  {msg.toolResult && (
                    <div style={testStyles.toolSection}>
                      <span style={testStyles.toolLabel}>Result:</span>
                      <pre style={testStyles.toolPre}>{JSON.stringify(msg.toolResult, null, 2)}</pre>
                    </div>
                  )}
                </div>
              </div>
            );
          }
          if (msg.type === 'scenarioResult') {
            return (
              <div key={msg.id} style={{
                ...testStyles.scenarioCard,
                borderColor: msg.passed ? '#86efac' : '#fca5a5'
              }}>
                <div style={testStyles.scenarioHeader}>
                  <span style={{
                    ...testStyles.scenarioBadge,
                    backgroundColor: msg.passed ? '#dcfce7' : '#fee2e2',
                    color: msg.passed ? '#166534' : '#991b1b'
                  }}>
                    {msg.passed ? 'PASS' : 'FAIL'}
                  </span>
                  <span style={{ fontWeight: '600', color: '#374151' }}>{msg.scenario}</span>
                  <span style={{ color: '#9ca3af', fontSize: '12px' }}>{msg.durationMs}ms</span>
                </div>
                {msg.error && (
                  <div style={{ color: '#dc2626', fontSize: '13px', marginTop: '8px' }}>
                    Error: {msg.error}
                  </div>
                )}
                {msg.assertions && msg.assertions.length > 0 && (
                  <div style={testStyles.assertionsList}>
                    {msg.assertions.map((a, i) => (
                      <div key={i} style={testStyles.assertionRow}>
                        <span style={{ color: a.passed ? '#16a34a' : '#dc2626' }}>
                          {a.passed ? '\u2713' : '\u2717'}
                        </span>
                        <span style={{ fontSize: '13px', color: '#374151' }}>{a.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          }
          if (msg.type === 'autoTestResult') {
            return (
              <div key={msg.id} style={{
                ...testStyles.scenarioCard,
                borderColor: msg.passed ? '#86efac' : '#fca5a5'
              }}>
                <div style={testStyles.scenarioHeader}>
                  <span style={{
                    ...testStyles.scenarioBadge,
                    backgroundColor: msg.passed ? '#dcfce7' : '#fee2e2',
                    color: msg.passed ? '#166534' : '#991b1b'
                  }}>
                    {msg.passed ? 'PASS' : 'FAIL'}
                  </span>
                  <span style={{ fontWeight: '600', color: '#374151' }}>{msg.scenario}</span>
                  {msg.score != null && (
                    <span style={{
                      fontSize: '13px',
                      fontWeight: '600',
                      color: msg.score >= 70 ? '#16a34a' : msg.score >= 50 ? '#d97706' : '#dc2626'
                    }}>
                      {msg.score}/100
                    </span>
                  )}
                  <span style={{ color: '#9ca3af', fontSize: '12px' }}>
                    {msg.turns} turns &middot; {(msg.durationMs / 1000).toFixed(1)}s
                  </span>
                </div>
                {msg.runId && (
                  <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px', fontFamily: 'monospace' }}>
                    Run ID: {msg.runId}
                  </div>
                )}
                {msg.evaluation?.summary && (
                  <div style={{ fontSize: '13px', color: '#374151', marginTop: '8px' }}>
                    {msg.evaluation.summary}
                  </div>
                )}
                {msg.error && (
                  <div style={{ color: '#dc2626', fontSize: '13px', marginTop: '8px' }}>
                    Error: {msg.error}
                  </div>
                )}
                {msg.evaluation?.issues?.length > 0 && (
                  <div style={testStyles.assertionsList}>
                    {msg.evaluation.issues.map((issue, i) => (
                      <div key={i} style={testStyles.assertionRow}>
                        <span style={{ color: '#dc2626' }}>{'\u2717'}</span>
                        <span style={{ fontSize: '13px', color: '#374151' }}>{issue}</span>
                      </div>
                    ))}
                  </div>
                )}
                {msg.toolAssertions?.length > 0 && (
                  <div style={testStyles.assertionsList}>
                    {msg.toolAssertions.map((a, i) => (
                      <div key={i} style={testStyles.assertionRow}>
                        <span style={{ color: a.passed ? '#16a34a' : '#dc2626' }}>
                          {a.passed ? '\u2713' : '\u2717'}
                        </span>
                        <span style={{ fontSize: '13px', color: '#374151' }}>{a.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          }
          if (msg.type === 'system') {
            return (
              <div key={msg.id} style={testStyles.systemRow}>
                <span style={testStyles.systemText}>{msg.text}</span>
              </div>
            );
          }
          return null;
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div style={testStyles.inputArea}>
        <div style={testStyles.inputRow}>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={sessionActive ? 'Type a patient message...' : 'Start a session first'}
            disabled={!sessionActive}
            style={testStyles.textInput}
          />
          <button
            onClick={handleSend}
            disabled={!sessionActive || !input.trim()}
            style={{
              ...testStyles.sendButton,
              opacity: !sessionActive || !input.trim() ? 0.5 : 1
            }}
          >
            Send
          </button>
        </div>
        <div style={testStyles.scenarioRow}>
          <select
            value={selectedScenario}
            onChange={(e) => setSelectedScenario(e.target.value)}
            style={testStyles.scenarioSelect}
            disabled={scenarioRunning}
          >
            <option value="">Select scenario...</option>
            {SCENARIOS.map(s => (
              <option key={s.name} value={s.name}>{s.label}</option>
            ))}
          </select>
          <button
            onClick={handleRunScenario}
            disabled={!selectedScenario || scenarioRunning}
            style={{
              ...testStyles.runButton,
              opacity: !selectedScenario || scenarioRunning ? 0.5 : 1
            }}
          >
            {scenarioRunning ? 'Running...' : 'Run Scenario'}
          </button>
        </div>
        <div style={{ ...testStyles.scenarioRow, marginTop: '8px' }}>
          <select
            value={selectedAutoScenario}
            onChange={(e) => setSelectedAutoScenario(e.target.value)}
            style={testStyles.scenarioSelect}
            disabled={autoTestRunning}
          >
            <option value="">Select AI patient test...</option>
            {AUTO_SCENARIOS_LIST.map(s => (
              <option key={s.name} value={s.name}>{s.label}</option>
            ))}
          </select>
          <select
            value={autoTestMode}
            onChange={(e) => setAutoTestMode(e.target.value)}
            style={{ ...testStyles.modeSelect, width: '100px' }}
            disabled={autoTestRunning}
          >
            <option value="mock">Mock</option>
            <option value="live">Live</option>
          </select>
          <button
            onClick={handleRunAutoTest}
            disabled={!selectedAutoScenario || autoTestRunning}
            style={{
              ...testStyles.runButton,
              backgroundColor: '#059669',
              opacity: !selectedAutoScenario || autoTestRunning ? 0.5 : 1
            }}
          >
            {autoTestRunning ? 'Running AI Test...' : 'Run AI Test'}
          </button>
        </div>

        {/* Push Slots Section */}
        <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '10px', paddingTop: '8px' }}>
          <button
            onClick={() => setPushSlotsExpanded(!pushSlotsExpanded)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px',
              color: '#6b7280', display: 'flex', alignItems: 'center', gap: '4px', padding: '2px 0'
            }}
          >
            <span style={{ fontSize: '10px' }}>{pushSlotsExpanded ? '\u25BC' : '\u25B6'}</span>
            Push Test Slots to EHR
          </button>
          {pushSlotsExpanded && (
            <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <select
                  value={slotProvider}
                  onChange={(e) => setSlotProvider(e.target.value)}
                  style={{ ...testStyles.scenarioSelect, flex: '1 1 180px' }}
                >
                  <option value="">Select provider...</option>
                  {providers.map(p => (
                    <option key={p.id} value={String(p.id)}>
                      {p.first_name} {p.last_name} (ID: {p.athena_provider_id})
                    </option>
                  ))}
                </select>
                <select
                  value={slotDepartment}
                  onChange={(e) => setSlotDepartment(e.target.value)}
                  style={{ ...testStyles.scenarioSelect, flex: '1 1 150px' }}
                >
                  <option value="">Select department...</option>
                  {departments.map(d => (
                    <option key={d.id} value={String(d.id)}>
                      {d.name} (Dept: {d.athena_department_id})
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                <input
                  type="date"
                  value={slotDate}
                  onChange={(e) => setSlotDate(e.target.value)}
                  style={{ ...testStyles.textInput, flex: '0 0 150px', padding: '6px 10px', fontSize: '13px' }}
                />
                <input
                  type="time"
                  value={slotTime}
                  onChange={(e) => setSlotTime(e.target.value)}
                  style={{ ...testStyles.textInput, flex: '0 0 100px', padding: '6px 10px', fontSize: '13px' }}
                />
                <input
                  type="text"
                  value={slotReason}
                  onChange={(e) => setSlotReason(e.target.value)}
                  placeholder="Appointment type/reason"
                  style={{ ...testStyles.textInput, flex: '1 1 200px', padding: '6px 10px', fontSize: '13px' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <label style={{ fontSize: '12px', color: '#6b7280' }}>Reason Code:</label>
                {ehrReasonCodes.length > 0 ? (
                  <select
                    value={slotReasonId}
                    onChange={(e) => setSlotReasonId(e.target.value)}
                    style={{ ...testStyles.scenarioSelect, flex: '1 1 200px' }}
                  >
                    <option value="">Select reason code...</option>
                    {ehrReasonCodes.map(r => (
                      <option key={r.id} value={String(r.ehr_reason_id)}>
                        {r.reason_text} (ID: {r.ehr_reason_id})
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="number"
                    value={slotReasonId}
                    onChange={(e) => setSlotReasonId(e.target.value)}
                    placeholder="e.g., 81"
                    style={{ ...testStyles.textInput, width: '70px', padding: '6px 8px', fontSize: '13px' }}
                  />
                )}
                <label style={{ fontSize: '12px', color: '#6b7280', marginLeft: '8px' }}>Appt Type:</label>
                {ehrAppointmentTypes.length > 0 ? (
                  <select
                    value={slotApptTypeId}
                    onChange={(e) => setSlotApptTypeId(e.target.value)}
                    style={{ ...testStyles.scenarioSelect, flex: '1 1 200px' }}
                  >
                    <option value="">Select appt type...</option>
                    {ehrAppointmentTypes.map(t => (
                      <option key={t.id} value={String(t.ehr_type_id)}>
                        {t.name} (ID: {t.ehr_type_id})
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="number"
                    value={slotApptTypeId}
                    onChange={(e) => setSlotApptTypeId(e.target.value)}
                    placeholder="e.g., 683"
                    style={{ ...testStyles.textInput, width: '70px', padding: '6px 8px', fontSize: '13px' }}
                  />
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <label style={{ fontSize: '12px', color: '#6b7280' }}>Duration:</label>
                <input
                  type="number"
                  value={slotDuration}
                  onChange={(e) => setSlotDuration(e.target.value)}
                  style={{ ...testStyles.textInput, width: '60px', padding: '6px 8px', fontSize: '13px' }}
                />
                <span style={{ fontSize: '12px', color: '#6b7280' }}>min</span>
                <label style={{ fontSize: '12px', color: '#6b7280', marginLeft: '8px' }}>Count:</label>
                <input
                  type="number"
                  value={slotCount}
                  onChange={(e) => setSlotCount(e.target.value)}
                  min="1"
                  max="10"
                  style={{ ...testStyles.textInput, width: '50px', padding: '6px 8px', fontSize: '13px' }}
                />
                <label style={{ fontSize: '12px', color: '#6b7280', marginLeft: '8px' }}>Interval:</label>
                <input
                  type="number"
                  value={slotInterval}
                  onChange={(e) => setSlotInterval(e.target.value)}
                  style={{ ...testStyles.textInput, width: '60px', padding: '6px 8px', fontSize: '13px' }}
                />
                <span style={{ fontSize: '12px', color: '#6b7280' }}>min</span>
                <button
                  onClick={handlePushSlots}
                  disabled={pushingSlots || !slotProvider || !slotDepartment || !slotDate}
                  style={{
                    ...testStyles.runButton,
                    backgroundColor: '#d97706',
                    marginLeft: 'auto',
                    opacity: pushingSlots || !slotProvider || !slotDepartment || !slotDate ? 0.5 : 1
                  }}
                >
                  {pushingSlots ? 'Pushing...' : 'Push Slots'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Simulate Patient Booking Section */}
        <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '10px', paddingTop: '8px' }}>
          <button
            onClick={() => setSimulateBookingExpanded(!simulateBookingExpanded)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px',
              color: '#6b7280', display: 'flex', alignItems: 'center', gap: '4px', padding: '2px 0'
            }}
          >
            <span style={{ fontSize: '10px' }}>{simulateBookingExpanded ? '\u25BC' : '\u25B6'}</span>
            Simulate Patient Booking
          </button>
          {simulateBookingExpanded && (
            <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <label style={{ fontSize: '12px', color: '#6b7280' }}>Open Slot:</label>
                <select
                  value={selectedSlot}
                  onChange={(e) => setSelectedSlot(e.target.value)}
                  style={{ ...testStyles.scenarioSelect, flex: '1 1 300px' }}
                >
                  <option value="">Select an open slot...</option>
                  {openSlots.map(s => (
                    <option key={s.athena_appointment_id} value={String(s.athena_appointment_id)}>
                      {s.appointment_date?.split('T')[0]} {s.appointment_date?.split('T')[1]?.substring(0,5) || ''} - {s.appointment_type || 'Unknown'} ({s.duration_minutes || '?'}min) [ID: {s.athena_appointment_id}]
                    </option>
                  ))}
                </select>
                {openSlots.length === 0 && (
                  <span style={{ fontSize: '11px', color: '#9ca3af', fontStyle: 'italic' }}>No open slots — push slots first</span>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <label style={{ fontSize: '12px', color: '#6b7280' }}>Athena Patient ID:</label>
                <input
                  type="number"
                  value={bookingPatientId}
                  onChange={(e) => setBookingPatientId(e.target.value)}
                  placeholder="e.g., 5"
                  style={{ ...testStyles.textInput, width: '100px', padding: '6px 8px', fontSize: '13px' }}
                />
                <span style={{ fontSize: '11px', color: '#9ca3af' }}>Patient demographics will be fetched from EHR</span>
                <button
                  onClick={handleSimulateBooking}
                  disabled={simulatingBooking || !selectedSlot || !bookingPatientId}
                  style={{
                    ...testStyles.runButton,
                    backgroundColor: '#059669',
                    marginLeft: 'auto',
                    opacity: simulatingBooking || !selectedSlot || !bookingPatientId ? 0.5 : 1
                  }}
                >
                  {simulatingBooking ? 'Booking...' : 'Book Patient'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const testStyles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: '#f9fafb'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 20px',
    backgroundColor: '#fff',
    borderBottom: '1px solid #e5e7eb'
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  headerTitle: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#111827'
  },
  statusBadge: {
    fontSize: '12px',
    fontWeight: '500',
    padding: '2px 10px',
    borderRadius: '12px'
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  modeSelect: {
    padding: '6px 10px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '13px',
    backgroundColor: '#fff',
    color: '#374151',
    cursor: 'pointer'
  },
  startButton: {
    padding: '8px 16px',
    backgroundColor: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  endButton: {
    padding: '8px 16px',
    backgroundColor: '#ef4444',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  messagesArea: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    color: '#9ca3af'
  },
  userRow: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  userBubble: {
    maxWidth: '70%',
    padding: '10px 14px',
    backgroundColor: '#3b82f6',
    color: '#fff',
    borderRadius: '16px 16px 4px 16px',
    fontSize: '14px',
    lineHeight: '1.4',
    whiteSpace: 'pre-wrap'
  },
  assistantRow: {
    display: 'flex',
    justifyContent: 'flex-start'
  },
  assistantBubble: {
    maxWidth: '70%',
    padding: '10px 14px',
    backgroundColor: '#e5e7eb',
    color: '#111827',
    borderRadius: '16px 16px 16px 4px',
    fontSize: '14px',
    lineHeight: '1.4',
    whiteSpace: 'pre-wrap'
  },
  toolCard: {
    maxWidth: '80%',
    backgroundColor: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    overflow: 'hidden',
    fontSize: '13px'
  },
  toolHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 12px',
    backgroundColor: '#f9fafb',
    borderBottom: '1px solid #f3f4f6'
  },
  toolIcon: {
    fontSize: '14px',
    color: '#6b7280'
  },
  toolName: {
    fontWeight: '600',
    color: '#374151'
  },
  toolBadge: {
    fontSize: '11px',
    fontWeight: '500',
    padding: '1px 6px',
    borderRadius: '4px',
    marginLeft: 'auto'
  },
  toolBody: {
    padding: '8px 12px'
  },
  toolSection: {
    marginBottom: '6px'
  },
  toolLabel: {
    fontSize: '11px',
    fontWeight: '600',
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  toolPre: {
    margin: '4px 0 0',
    padding: '6px 8px',
    backgroundColor: '#f9fafb',
    borderRadius: '4px',
    fontSize: '12px',
    fontFamily: 'monospace',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    maxHeight: '120px',
    overflowY: 'auto',
    color: '#374151'
  },
  scenarioCard: {
    backgroundColor: '#fff',
    border: '2px solid',
    borderRadius: '8px',
    padding: '12px 16px',
    maxWidth: '90%'
  },
  scenarioHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  scenarioBadge: {
    fontSize: '12px',
    fontWeight: '700',
    padding: '2px 10px',
    borderRadius: '4px'
  },
  assertionsList: {
    marginTop: '10px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  assertionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  systemRow: {
    display: 'flex',
    justifyContent: 'center',
    padding: '4px 0'
  },
  systemText: {
    fontSize: '12px',
    color: '#9ca3af',
    fontStyle: 'italic'
  },
  inputArea: {
    padding: '12px 20px',
    backgroundColor: '#fff',
    borderTop: '1px solid #e5e7eb'
  },
  inputRow: {
    display: 'flex',
    gap: '8px',
    marginBottom: '8px'
  },
  textInput: {
    flex: 1,
    padding: '10px 14px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none'
  },
  sendButton: {
    padding: '10px 20px',
    backgroundColor: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  scenarioRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center'
  },
  scenarioSelect: {
    flex: 1,
    padding: '8px 10px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '13px',
    backgroundColor: '#fff',
    color: '#374151',
    cursor: 'pointer'
  },
  runButton: {
    padding: '8px 16px',
    backgroundColor: '#8b5cf6',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer'
  }
};

export default VoiceAgentTab;
